import styled from "@emotion/styled";
import { Button } from "react-aria-components";
import { IMDSButtonProps } from "./button.h";
import { Theme } from "@emotion/react";

export const StyledButton = styled(Button)<IMDSButtonProps>(props => ({
  display: "flex",
  textWrap: "nowrap",
  flexDirection: props.orientation === "horizontal" ? "row" : "column",
  gap: props.theme.spacing.xs,
  justifyContent: "center",
  alignItems: "center",
  flexWrap: "nowrap",
  borderRadius: props.theme.borderRadius.medium,
  outlineWidth: 1,
  outlineStyle: "solid",
  cursor: "pointer",
  transition: "0.25s ease",
  userSelect: "none",
  boxShadow: props.theme.shadows.sm,

  fontWeight: getFontWeight(props),
  fontSize: getFontSize(props),
  padding: getPadding(props),
  lineHeight: getLineHeight(props),

  "& .mds-btn-icon": {
    color: props.theme.colors.grey.x0,
  },

  ...getColors(props),
  ...getExtras(props),
}));

const getPadding = (props: TButtonProps): string => {
  switch (props.size) {
    case "sm":
      return props.theme.spacing.sm;
    case "lg":
      return props.theme.spacing.smd;
    case "xs":
      return props.theme.spacing.xs;
    default:
      return props.theme.spacing.smd;
  }
};

const getExtras = (props: TButtonProps) => {
  switch (props.size) {
    case "lg":
      return {
        "& .mds-btn-icon": {
          fontSize: 16,
        },
        "& .mds-btn-label": {
          fontSize: 16,
          lineHeight: "24px",
        },
      };
  }
};

const getColors = (props: TButtonProps) => {
  const baselineStyle = {
    color: props.theme.colors.grey.x800,
    backgroundColor: props.theme.colors.white,
    outlineColor: props.theme.colors.grey.x100,

    "&[data-hovered]:not([data-disabled])": {
      outlineColor: props.theme.colors.grey.x200,
      color: props.theme.colors.grey.x600,
      transition: "0.2s all",
    },

    "&[data-pressed]:not([data-disabled])": {
      backgroundColor: props.theme.colors.grey.x25,
      outlineColor: props.theme.colors.grey.x200,
      color: props.theme.colors.grey.x600,
      boxShadow: "none",
    },

    "&[data-disabled]": {
      cursor: "not-allowed",
      backgroundColor: props.theme.colors.grey.x300,
    },
  };

  switch (props.color) {
    case "primary":
      return {
        backgroundColor: props.theme.colors.primary.x500,
        color: props.theme.colors.grey.x0,

        "&[data-disabled]": {
          opacity: 0.4,
          cursor: "not-allowed",
        },
      };
    case "white":
      return {
        backgroundColor: props.theme.colors.white,
        color: props.theme.colors.grey.x600,

        "&[data-disabled]": {
          opacity: 0.4,
          cursor: "not-allowed",
        },
      };
    case "contrast":
      return {
        backgroundColor: props.theme.colors.grey.x700,
        outlineColor: props.theme.colors.grey.x500,
        color: props.theme.colors.grey.x100,

        "&[data-hovered]:not([data-disabled])": {
          outlineColor: props.theme.colors.grey.x400,
          backgroundColor: props.theme.colors.grey.x700,
        },

        "&[data-pressed]": {
          outlineColor: props.theme.colors.grey.x400,
          backgroundColor: props.theme.colors.grey.x600,
        },

        "&[data-disabled]": {
          opacity: 0.4,
          cursor: "not-allowed",
          outlineColor: props.theme.colors.grey.x500,
          backgroundColor: props.theme.colors.grey.x700,
        },
      };
    case "highlight":
      return {
        ...baselineStyle,
        outline: "none",
        boxShadow: "none",

        "&[data-hovered]:not([data-disabled])": {
          backgroundColor: props.theme.colors.grey.x50,
          outline: "none",
        },

        "&[data-pressed]:not([data-disabled])": {
          backgroundColor: props.theme.colors.grey.x25,
          outline: "none",
        },

        "&[data-disabled]": {
          opacity: 0.4,
          cursor: "not-allowed",
          backgroundColor: props.theme.colors.white,
        },
      };

    case "transparent":
      return {
        ...baselineStyle,
        backgroundColor: "transparent",
        outline: "none",
        boxShadow: "none",

        "&[data-hovered]": {
          backgroundColor: props.theme.colors.grey.x50,
          outline: "none",
        },

        "&[data-pressed]": {
          backgroundColor: props.theme.colors.grey.x25,
          outline: "none",
        },

        "&[data-disabled]": {
          opacity: 0.4,
          cursor: "not-allowed",
        },
      };

    default:
      return baselineStyle;
  }
};

const getFontSize = (props: TButtonProps) => {
  switch (props.size) {
    case "xs":
      return props.theme.fontSizes.xxsmall;
    default:
      return props.theme.fontSizes.small;
  }
};

const getFontWeight = (props: TButtonProps) => {
  switch (props.size) {
    case "xs":
      return props.theme.fontWeights.regular;
    default:
      return props.theme.fontWeights.semiBold;
  }
};

const getLineHeight = (props: TButtonProps) => {
  switch (props.size) {
    case "xs":
      return props.theme.lineHeights.xsmall;
    default:
      "auto";
  }
};

type TButtonProps = IMDSButtonProps & { theme: Theme };
